<i18n src="./resources/locales.json"></i18n>
<template>
  <div class="table-container reverse-sort">
    <div>
      <EcsTable
        :key="componentKey"
        :headers="displayedHeaders"
        :items="summaryData"
        :options="options"
        :disable-sort="disableSort"
        :custom-sort="customSort"
        :custom-filter="customFilter"
        :search="emptySearchPlaceholder"
        :disable-pagination="disablePagination"
        :id-field="'agentId'"
        filterable
        :context-menu-event="contextMenuEvent"
        @optionsUpdate="onOptionsUpdate"
      />
      <EcsShowColumns
        :items="supervisorSummaryTable"
        :display="displayShowColumns"
        :dialog-close="onDialogClose"
        :dialog-accept="onDialogAccept"
        :dialog-restore="onDialogRestore"
        :agents="agents"
        :hidden-ids="hiddenIds"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EcsTable from '@/components/ecs-table/EcsTable';
import AgentService from '@/services/agentService';
import InteractionService from '@/services/interactionService';
import SupervisorService from '@/services/supervisorService';
import { AGENT_STATE } from '@/constants/agent/constants';
import { COLUMN_TYPE } from '@/constants/table';
import EcsShowColumns from '@/components/ecs-wallboard-card/ecs-supervised-agents-slide/EcsShowColumns';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';
import { defaultSupervisedAgentsTable } from '@/store/modules/supervisor';

const ERROR_MESSAGE_LABEL_KEY = 'menus.errorMessage';

export default {
  name: 'EcsSupervisedAgentsSlide',
  components: {
    EcsTable,
    EcsShowColumns
  },

  mixins: [EcsKpiFormatter],

  props: {
    card: {
      type: Object,
      default() {
        return {};
      }
    },
    slide: {
      type: Object,
      default() {
        return {};
      }
    }
  },

  data() {
    return {
      contextMenuEvent: 'supervised-agent-context-menu',
      emptySearchPlaceholder: '"',
      componentKey: 1,
      disableSort: false,
      displayShowColumns: false,
      disablePagination: true,
      options: {
        page: 1,
        sortBy: [''],
        sortDesc: [false]
      },
      agent: null
    };
  },

  computed: {
    ...mapGetters('SUPERVISOR', ['getSupervisedAgents', 'selectedSupervisorWallboard']),

    ...mapGetters('WALLBOARD', ['getWallboardCards']),

    ...mapGetters('REFERENCES', ['availableAgentStates', 'availableCustomBreakStates', 'supervisorKPIsDescription']),

    ...mapGetters('CONTACTS', ['getAgent']),

    summaryData() {
      /* Prevent case sensitive issues */
      return this.getSupervisedAgents.filter(agent => !this.hiddenIds.includes(agent.agentId));
    },
    supervisorSummaryTable() {
      return this.slide.summaryTable.map(header => {
        const KPIDescription = this.KPIDescriptions.find(KPIDescription => {
          return KPIDescription.name === header.value;
        });
        const description = KPIDescription ? KPIDescription.description : '';

        return {
          ...header,
          text: this.$t(header.text),
          description: description
        };
      });
    },
    hiddenIds() {
      return this.slide.summaryTableSettings.hiddenIds;
    },
    agents() {
      return this.getSupervisedAgents.map(({ agentId, agentName }) => ({
        agentId,
        agentName
      }));
    },
    filterableHeaders() {
      return this.supervisorSummaryTable.filter(header => header.filterable);
    },
    operators() {
      if (!this.filterColumn) {
        return [];
      }
      const header = this.supervisorSummaryTable.find(header => header.value === this.filterColumn);
      return this.filterOperators.filter(operator => operator.type.includes(header.type));
    },

    KPIDescriptions() {
      // Temporal KPI Description to DisplayedHeader Mapping
      const tempMapping = {
        AgentAnsweredAsConsultOrConference: 'totalAnsweredAsConsult',
        AgentAverageTreatingTime: 'averageHandlingTime',
        AgentBPAnsweredCalls: 'businessProcessHandled',
        AgentTotalIncomingCalls: 'totalIncomingInteractions',
        AgentTotalCallbacksReschedule: 'totalCallbacksRequested',
        AgentBackOfficeTime: 'totalBackOfficeTime',
        AgentBreakTime: 'totalBreakTime',
        AgentCurrentHandling: 'currentlyHandling',
        AgentLastLoginTime: 'lastLoginTime',
        AgentLoginTimeSpan: 'totalLoginTime',
        AgentTotalBusyTime: 'totalBusyTime',
        AgentTotalNoAnswerTime: 'totalNoAnswer',
        AgentTotalHandlingOutgoingTime: 'totalHandlingOutgoing',
        AgentMissedCalls: 'totalMissed',
        AgentRejectedCalls: 'businessProcessRejected',
        AgentTimeInState: 'agentStateTime',
        AgentTotalConsultOrConference: 'totalConsultOrConferenceInitiated',
        AgentTotalIncomingPrivateCalls: 'totalIncomingPrivate',
        AgentTotalNotReadyTime: 'totalNotReadyTime',
        AgentTotalOutgoingExternalBPCalls: 'totalOutgoingExternal',
        AgentTotalOutgoingInternalBPCalls: 'totalOutgoingInternal',
        AgentTotalOutgoingPrivateCalls: 'totalOutgoingPrivate',
        AgentTotalReadyTime: 'totalReadyTime',
        AgentTotalTransfer: 'totalTransfer',
        AgentWorkingTime: 'totalHandlingTime',
        AgentCurrentTimeInWrapUp: 'currentTimeInWrapUp',
        AgentWrapUpTimeInShift: 'totalWrapUpTime',
        AgentASA: 'averageRingTime',
        AgentTotalUnsuccessfulDialingAttempts: 'totalUnsuccessfulDialingAttempts',
        AgentTotalUnsuccessfulDialingTime: 'totalUnsuccessfulDialingTime',
        AgentBusyToLoginTimeRatio: 'busyToLoginTimeRatio',
        AgentTotalDialingTime: 'totalDialingTime',
        AgentCurrentHandlingOutgoingTime: 'currentHandlingOutgoingTime',
        AgentCurrentBreakTime: 'currentTimeInBreak',
        AgentCurrentBusyTime: 'currentBusyTime',
        AgentCurrentReadyTime: 'currentReadyTime',
        AgentCurrentBackOfficeTime: 'currentBackOfficeTime',
        AgentCurrentInteractionHandlingTime: 'currentInteractionHandlingTime',
        AgentTotalPreviewTime: 'totalPreviewTime',
        AgentAveragePreviewTime: 'averagePreviewTime',
        AgentCurrentPreviewTime: 'currentPreviewTime'
      };

      return this.supervisorKPIsDescription.map(KPIDescription => {
        return {
          name: tempMapping[KPIDescription.name] || KPIDescription.name,
          description: KPIDescription.description
        };
      });
    },

    displayedHeaders() {
      return this.supervisorSummaryTable
        .filter(header => header.show)
        .sort((a, b) => a.columnIndex - b.columnIndex)
        .map(header => {
          return {
            ...header,
            text: this.$t(header.text)
          };
        });
    },

    dashboardId() {
      return this.selectedSupervisorWallboard._id;
    }
  },

  created() {
    this.requestSupervisorKPIsDescription();
  },

  mounted() {
    this.$root.$on('update-summary-table-col-width', this.updateSupervisorSummaryColWidth);
    this.$root.$on('table-disable-sort', this.disableTableSort);
    this.$root.$on('table-enable-sort', this.enableTableSort);
    this.$root.$on(this.contextMenuEvent, this.showContextMenu);

    if ((this.supervisorSummaryTable, this.supervisorSummaryTable.length === 0)) {
      const payload = { supervisorSummaryTable: defaultSupervisedAgentsTable };
      this.updateSupervisorSummary(payload);
    }

    this.fallbackSupervisorSummaryTable();

    this.$el.addEventListener('scroll', this.stickyFirstColumn);
  },

  beforeDestroy() {
    this.$el.removeEventListener('scroll', this.stickyFirstColumn);
  },

  methods: {
    ...mapActions('WALLBOARD', ['updateWallboardCards']),

    ...mapActions('REFERENCES', ['requestSupervisorKPIsDescription']),

    onOptionsUpdate(newOptions) {
      this.options = { ...newOptions };
    },
    formatTable(items, sortBy, isDesc) {
      return items.sort((a, b) => this.tableSort(a, b, sortBy, isDesc));
    },
    customSort(items, index, isDesc) {
      if (!items || !index) {
        return items;
      }
      items.sort((a, b) => {
        if (!a.agentId) {
          return -1;
        }
        if (!b.agentId) {
          return 1;
        }
        const typeOfValue = typeof a[index];
        if (typeOfValue === 'string') {
          return this.sortString(a, b, index, isDesc[0]);
        } else if (typeOfValue === 'number') {
          return this.sortNumber(a, b, index, isDesc[0]);
        } else {
          return null;
        }
      });
      return items;
    },
    sortString(a, b, index, isDesc) {
      const str1 = a[index];
      const str2 = b[index];
      if (!isDesc) {
        return str1.localeCompare(str2);
      } else {
        return str2.localeCompare(str1);
      }
    },
    sortNumber(a, b, index, isDesc) {
      if (!isDesc) {
        return b[index] - a[index];
      } else {
        return a[index] - b[index];
      }
    },
    customFilter(value, search, item) {
      let keepRow = true;
      this.supervisorSummaryTable.every(header => {
        if (!Object.hasOwn(header.customFilter, 'inRange')) {
          header.customFilter.inRange = true;
        }
        if (header.customFilter.enabled) {
          keepRow = this.applyOperator(
            item[header.value],
            header.type,
            header.customFilter.value,
            header.customFilter.inRange,
            header.value
          );
        }
        return keepRow;
      });
      return keepRow;
    },
    applyOperator(value, filterType, filterValue, filterInRange, name) {
      if (
        filterType === COLUMN_TYPE.NUMBER ||
        filterType === COLUMN_TYPE.PERCENTAGE ||
        filterType === COLUMN_TYPE.TIME ||
        filterType === COLUMN_TYPE.TIME_FROM_NOW ||
        filterType === COLUMN_TYPE.DATE
      ) {
        let filterValue1, filterValue2;
        if (filterType === COLUMN_TYPE.TIME || filterType === COLUMN_TYPE.TIME_FROM_NOW) {
          value = new Date('1970-01-01T' + value).getTime();
          filterValue1 = filterValue[0] ? new Date('1970-01-01T' + filterValue[0]).getTime() : -1;
          filterValue2 = filterValue[1] ? new Date('1970-01-01T' + filterValue[1]).getTime() : -1;
        } else if (filterType === COLUMN_TYPE.DATE) {
          value = new Date(value).getTime();
          filterValue1 = filterValue[0] ? new Date(filterValue[0]).getTime() : -1;
          filterValue2 = filterValue[1] ? new Date(filterValue[1]).getTime() : -1;
        } else if (filterType === COLUMN_TYPE.NUMBER || filterType === COLUMN_TYPE.PERCENTAGE) {
          value = Number(value);
          if (filterType === COLUMN_TYPE.PERCENTAGE) {
            value *= 100;
          }
          filterValue1 = filterValue[0] ? Number(filterValue[0]) : -1;
          filterValue2 = filterValue[1] ? Number(filterValue[1]) : -1;
        }

        if (filterValue1 >= 0 && filterValue2 >= 0) {
          if (filterInRange) {
            return filterValue1 <= value && value <= filterValue2;
          } else {
            return filterValue1 > value || value > filterValue2;
          }
        } else if (filterValue1 >= 0) {
          return filterValue1 <= value;
        } else if (filterValue2 >= 0) {
          return value <= filterValue2;
        }
      } else if (filterType === COLUMN_TYPE.INCLUDES) {
        let valueIncluded = false;
        value.split(',').every(v => {
          if (filterValue.includes(v.trim())) {
            valueIncluded = true;
          }
        });
        return valueIncluded;
      } else if (filterType === COLUMN_TYPE.OPEN) {
        let valueIncluded = false;
        filterValue.split(',').every(fv => {
          if (value.toLowerCase().includes(fv.trim().toLowerCase())) {
            valueIncluded = true;
          }
        });
        return valueIncluded;
      }

      return false;
    },
    showColumns() {
      this.displayShowColumns = true;
    },
    onDialogClose() {
      this.displayShowColumns = false;
    },
    onDialogAccept(headers, hiddenIds) {
      this.displayShowColumns = false;

      const payload = {
        hiddenIds,
        supervisorSummaryTable: headers
      };

      this.updateSupervisorSummary(payload);
    },
    onDialogRestore() {
      this.displayShowColumns = false;

      const payload = {
        hiddenIds: [],
        supervisorSummaryTable: defaultSupervisedAgentsTable
      };

      this.updateSupervisorSummary(payload);
    },
    forceUpdate() {
      this.componentKey += 1;
    },
    updateSupervisorSummary(payload) {
      const updatedWallboardCards = this.getWallboardCards.map(card => {
        if (card._id === this.card._id) {
          const cardSlides = card.slides.map(slide => {
            if (slide._id === this.slide._id) {
              if (payload.hiddenIds) {
                slide.summaryTableSettings = {
                  hiddenIds: payload.hiddenIds
                };
              }
              if (payload.supervisorSummaryTable) {
                slide.summaryTable = payload.supervisorSummaryTable;
              }
            }
            return slide;
          });
          card.slides = cardSlides;
        }
        return card;
      });
      this.updateWallboardCards({ cards: updatedWallboardCards, dashboardId: this.dashboardId });
    },
    updateSupervisorSummaryColWidth(childEl, index, width) {
      if (this.$el.contains(childEl)) {
        const headers = this.supervisorSummaryTable;
        headers[this.displayedHeaders[index].columnIndex].width = width;

        const payload = {
          supervisorSummaryTable: headers
        };
        this.updateSupervisorSummary(payload);
      }
    },

    enableTableSort() {
      setTimeout(() => {
        this.disableSort = false;
      }, 1);
    },

    disableTableSort() {
      this.disableSort = true;
    },

    stickyFirstColumn(event) {
      const scrollTop = event.srcElement.scrollTop;

      event.srcElement.querySelectorAll('td:nth-child(1)').forEach(function(element) {
        element.style.transform = 'translateY(-' + scrollTop + 'px)';
      });
    },

    fallbackSupervisorSummaryTable() {
      const headers = this.supervisorSummaryTable;
      let headersChanged = false;

      headers.map(header => {
        const defaultHeader = defaultSupervisedAgentsTable.find(
          supervisedAgentHeader => header.value === supervisedAgentHeader.value
        );

        if (!('width' in header)) {
          header.width = defaultHeader.width;
          headersChanged = true;
        }
        if (!('show' in header)) {
          header.show = 'hide' in header ? !header.hide : defaultHeader.show;
          headersChanged = true;
        }
        if (!('customFilter' in header)) {
          header.customFilter = defaultHeader.customFilter;
          headersChanged = true;
        }

        if (header.type !== defaultHeader.type) {
          header.type = defaultHeader.type;
          headersChanged = true;
        }

        return header;
      });

      if (headersChanged) {
        const payload = {
          supervisorSummaryTable: headers
        };
        this.updateSupervisorSummary(payload);
      }
    },

    getInitialMenuItem() {
      return {
        label: this.$t('menus.stateMenu'),
        isDisabled: false,
        menu: this.availableAgentStates
          .filter(state => state.id !== this.agent.state.id)
          .map(item => {
            return {
              label: item.name,
              id: item.id,
              isDisabled: false,
              action: item.id !== AGENT_STATE.break ? this.changeAgentStatus : null,
              menu:
                item.id !== AGENT_STATE.break
                  ? null
                  : this.availableCustomBreakStates
                      .filter(state => state.id !== this.agent.state.id)
                      .map(i => {
                        return { label: i.name, id: i.id, action: this.changeAgentStatus, isDisabled: false };
                      })
            };
          })
      };
    },

    showContextMenu(event, item) {
      this.agent = this.getAgent(item.agentId);
      const position = { x: event.clientX, y: event.clientY };
      const promises = [
        AgentService.getAgentById(this.agent.id),
        InteractionService.getAgentInteractions(this.agent.id),
        SupervisorService.getAnotherAgentLoadAllowances(this.agent.id)
      ];
      const menu = [];

      this.$root.$emit('open-context-menu', {
        menu: [
          {
            label: this.$t('menus.stateMenu'),
            isLoading: true
          }
        ],
        position
      });

      Promise.all(promises).then(([agent, interactions, loadAllowances]) => {
        menu.push(this.getInitialMenuItem());

        const agentLoadAllowances = loadAllowances.data
          .filter(allowance => allowance.id !== agent.data.loadAllowanceId)
          .map(item => {
            return { label: item.name, id: item.id, action: this.changeAgentLoadAllowances, isDisabled: false };
          });
        if (agentLoadAllowances.length > 0) {
          menu.push({
            label: this.$t('menus.allowanceMenu'),
            isDisabled: false,
            menu: agentLoadAllowances
          });
        }

        if (interactions.data.length > 0) {
          menu.push({
            label: this.$t('menus.closeAllInteractions'),
            id: agent.data.id,
            action: this.closeAllInteractions
          });
        }

        this.$root.$emit('open-context-menu', { menu, position });
      });
    },

    changeAgentStatus(statusId) {
      SupervisorService.updateAnotherAgentState(this.agent.id, statusId).catch(() => {
        this.showSnackBar(this.$t(ERROR_MESSAGE_LABEL_KEY), 'error');
      });
    },

    changeAgentLoadAllowances(allowanceId) {
      SupervisorService.updateAnotherAgentLoadAllowance(this.agent.id, allowanceId).catch(() => {
        this.showSnackBar(this.$t(ERROR_MESSAGE_LABEL_KEY), 'error');
      });
    },

    closeAllInteractions(agentId) {
      AgentService.closeAllInteractions(agentId);
    }
  }
};
</script>

<style src="../scoped.css" scoped></style>

<style>
.table-container::-webkit-scrollbar {
  position: absolute;
  z-index: 3;
}

.table-container table {
  position: relative;
  z-index: 2;
  clip-path: inset(0 0 0 0);
}

.table-container table tr th:first-child,
.table-container table td:first-child {
  position: fixed !important;
  z-index: 1;
  align-content: center;
  background-color: var(--v-surface-lighten1);
  border-right: 1px solid var(--v-surface-lighten2);
}

.table-container table td:first-child {
  padding: 0 !important;
}

.table-container table tr:hover td:first-child {
  background: #616161;
}

.table-container table td:first-child > div {
  padding: 0 16px;
}

.reverse-sort .v-data-table-header th.active .v-data-table-header__icon,
.reverse-sort .v-data-table-header th:hover .v-data-table-header__icon {
  transform: rotate(-180deg);
}

.reverse-sort .v-data-table-header th.desc .v-data-table-header__icon {
  transform: none;
}
</style>
